
import { SWRConfiguration, SWRResponse } from "swr";

import { GetParticipantSourceDataFindings, GetSourceDataFindingsResponse, SourceDataFinding, UpdatedSourceDataFindingEvent } from "@/api/dtos";
import { AppSettings } from "@/AppSettings";
import useGet from "@/hooks/api/useGet";
import { HubConnection } from "@microsoft/signalr/dist/esm";
import { useCallback, useMemo } from "react";
import useSignalROnMessage from "../api/useSignalROnMessage";

const getData = (response?: SWRResponse<GetSourceDataFindingsResponse, GetSourceDataFindingsResponse>) => response?.data?.data;
const getError = (response?: SWRResponse<GetSourceDataFindingsResponse, GetSourceDataFindingsResponse>) => (response?.error ?? response?.data)?.responseStatus;
const getAdditionalState = (response?: SWRResponse<GetSourceDataFindingsResponse, GetSourceDataFindingsResponse>) => ({
    itemMap: response?.data?.data?.reduce<Record<string, Record<number, Record<string, Record<string, Record<number, Record<string, SourceDataFinding[]>>>>>>>((a, b) => {
        if (!b?.studyEventOid || !b?.studyEventRepeat || !b?.caseReportFormOid || !b?.itemName) {
            return a;
        }
        
        if (!a[b.studyEventOid]) {
            a[b.studyEventOid] = {};
        }

        if (!a[b.studyEventOid][b.studyEventRepeat]) {
            a[b.studyEventOid][b.studyEventRepeat] = {};
        }


        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid] = {};
        }
        
        const itemGroup = b.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey;

        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup] = {};
        }

        const itemGroupRepeat = b.itemGroupRepeat ?? 1;

        
        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat] = {};
        }
        
        if (!a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName]) {
            a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName] = [];
        }

        a[b.studyEventOid][b.studyEventRepeat][b.caseReportFormOid][itemGroup][itemGroupRepeat][b.itemName].push(b);

        return a;
    },{})
});

export const useParticipantSourceDataFindings = (participantId?: number, configuration?: SWRConfiguration | undefined) => {
    const request = useMemo(() => {
        if (!participantId) {
            return undefined;
        }

        return () => {
            return new GetParticipantSourceDataFindings({ participantId });
        }
    }, [participantId]);


    const output = useGet(request, { getData, getError, getAdditionalState }, configuration);

    const onMessage = useCallback(async (connection: HubConnection, data: UpdatedSourceDataFindingEvent) => {
        if (
            participantId === data.participantId
        )
        {
            await output.refresh();
        }

    }, [
        output.refresh,
        participantId
    ]);

    useSignalROnMessage(AppSettings.channels.finding, onMessage);

    return output;
}

export default useParticipantSourceDataFindings;